import React, { useState, useEffect } from "react";
import MyModal from "components/MyModal";
import * as yup from "yup";
import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  Select,
} from "@chakra-ui/react";
import Alert from "components/Alert";
import {
  useGetOrganizationsQuery,
  useGetProfileQuery,
  useCreateOrganisationRelationshipsMutation
} from "redux/services/membership";
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE, Roles } from "common";
import { getOrganisationMembership } from "utils";
import env from "env";
import { CreateOrganisationRelationshipsRequest } from "redux/services/membership/types";

type TApprovalProcessSetupModalProps = {
  visible?: boolean;
  data?: {
    ownerOrgId?: string;
    executorOrgId?: string;
  };
  showCancelBtn?: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

const schema = yup.object().shape({
  orgId: yup.string().required("Payroll Service Provider Name is required"),
});

const ApprovalProcessSetupModal: React.FC<TApprovalProcessSetupModalProps> = ({
  visible,
  data,
  showCancelBtn = false,
  onClose,
  onSuccess,
}) => {
  const { data: profileData } = useGetProfileQuery();
  const membershipOrganisation = getOrganisationMembership(
    profileData?.data?.memberships
  );

  const [
    createOrganisationRelationships,
    {
      isSuccess: isCreateOrganisationRelationshipsSuccess,
      isError: isCreateOrganisationRelationshipsError,
      error: createOrganisationRelationshipsError,
      isLoading: isCreateOrganisationRelationshipsLoading,
    },
  ] = useCreateOrganisationRelationshipsMutation();

  const { data: organisationsData, isLoading: isGetOrganisationsLoading } =
    useGetOrganizationsQuery(
      {
        pageSize: DEFAULT_PAGE_SIZE * 100,
        pageNum: DEFAULT_PAGE,
        orgRole: env.app.b2bPortalPSPRole,
      },
      { skip: !visible }
    );

  const organisationOptions =
    organisationsData?.data?.map(({ organisation }) => ({
      label: organisation.companyName || organisation.orgId,
      value: organisation.orgId,
      data: organisation,
    })) || [];

  const [formData, setFormData] = useState<{
    orgId: string;
  }>({
    orgId: "",
  });

  const [errors, setErrors] = useState<{
    orgId?: string;
  }>({});

  const handleChange = (e: any, useTrim?: boolean) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const validateForm = async () => {
    if (data?.executorOrgId) return true;

    try {
      await schema.validate(formData, { abortEarly: false });
      setErrors({});
      return true;
    } catch (err: any) {
      const validationErrors: any = {};
      err.inner.forEach((error: any) => {
        validationErrors[error.path] = error.message;
      });
      setErrors(validationErrors);
      return false;
    }
  };

  const handleSubmit = async () => {
    const isValid = await validateForm();

    if (isValid) {
      const body: CreateOrganisationRelationshipsRequest = {
        orgToken: membershipOrganisation?.token,
        ownerOrgId: data?.ownerOrgId,
        executorOrgId: data?.executorOrgId ?? formData.orgId,
        roles: [
          Roles.BusinessOwner,
          Roles.BusinessApprover,
        ]
      };

      await createOrganisationRelationships(body);
    }
  };

  useEffect(() => {
    if (isCreateOrganisationRelationshipsSuccess) {
      setFormData({ orgId: "" });
      onClose();
      onSuccess();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreateOrganisationRelationshipsSuccess]);

  useEffect(() => {
    if (data?.executorOrgId && data?.ownerOrgId) {
      setFormData({ orgId: data.executorOrgId });
      handleSubmit();
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <MyModal
      isOpen={!!visible}
      onClose={onClose}
      title="Approval Process Setup"
      submitBtnText="Save"
      onSubmit={() => handleSubmit()}
      scrollBehavior="inside"
      disabledCloseOutSide
      loadingSubmit={isCreateOrganisationRelationshipsLoading}
      disableSubmit={
        isGetOrganisationsLoading || isCreateOrganisationRelationshipsLoading
      }
      closeable={false}
      showCancelBtn={showCancelBtn}
    >
      {isCreateOrganisationRelationshipsError && (
        <Alert
          data-testid="alert-error"
          status="error"
          mb="10px"
          title={
            (createOrganisationRelationshipsError as any)?.data?.errors
              ?.map((err: any) => err.message)
              ?.join(", ") ?? "Something went wrong!"
          }
        />
      )}

      <FormControl mb="12px" isInvalid={!!errors.orgId}>
        <FormLabel>Payroll Service Provider Name</FormLabel>
        <Select
          name="orgId"
          data-testid="select-orgId"
          isRequired
          onChange={handleChange}
          value={formData.orgId}
        >
          <option value="" disabled>
            --- Select a Payroll Service Provider ---
          </option>
          {organisationOptions.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </Select>

        {errors.orgId && (
          <FormErrorMessage fontSize={12}>{errors.orgId}</FormErrorMessage>
        )}
      </FormControl>
    </MyModal>
  );
};

export default ApprovalProcessSetupModal;
